import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const HorairesPage = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  // Time range: 6h to 21h (visual scale)
  const SCALE_START = 6;
  const SCALE_END = 21;
  const SCALE_RANGE = SCALE_END - SCALE_START;

  const days = [
    { day: isFr ? 'Lundi' : 'Monday', short: 'Lu', hours: '6h30 - 20h30', open: true, start: 6.5, end: 20.5, icon: Flame },
    { day: isFr ? 'Mardi' : 'Tuesday', short: 'Ma', hours: '6h30 - 20h30', open: true, start: 6.5, end: 20.5, icon: Zap },
    { day: isFr ? 'Mercredi' : 'Wednesday', short: 'Me', hours: '6h30 - 20h30', open: true, start: 6.5, end: 20.5, icon: Target },
    { day: isFr ? 'Jeudi' : 'Thursday', short: 'Je', hours: '7h30 - 20h30', open: true, start: 7.5, end: 20.5, icon: Dumbbell },
    { day: isFr ? 'Vendredi' : 'Friday', short: 'Ve', hours: '6h30 - 20h', open: true, start: 6.5, end: 20, icon: Trophy },
    { day: isFr ? 'Samedi' : 'Saturday', short: 'Sa', hours: '8h30 - 14h30', open: true, start: 8.5, end: 14.5, icon: Heart },
    { day: isFr ? 'Dimanche' : 'Sunday', short: 'Di', hours: isFr ? 'Ferme' : 'Closed', open: false, start: 0, end: 0, icon: X },
  ];

  const now = new Date();
  const todayIdx = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const currentHour = now.getHours() + now.getMinutes() / 60;

  // Calculate total open hours per week
  const totalHours = days.reduce((sum, d) => sum + (d.open ? d.end - d.start : 0), 0);

  const getBarLeft = (start) => ((start - SCALE_START) / SCALE_RANGE) * 100;
  const getBarWidth = (start, end) => ((end - start) / SCALE_RANGE) * 100;

  return (
    <main className="min-h-screen bg-black relative overflow-hidden" data-testid="horaires-page">
      <SEOHead
        title="Horaires d'Ouverture | Body-Up Personal Training Nyon"
        description="Horaires Body-Up Nyon : Lundi-Mercredi 6h30-20h30, Jeudi 7h30-20h30, Vendredi 6h30-20h, Samedi 8h30-14h30. Route des Tattes d'Oie 95, 1260 Nyon. Sur rendez-vous."
        path="/horaires"
      />
      <style>{`
        @keyframes hw-enter { from { opacity: 0; transform: translateY(40px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes hw-bar { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes hw-glow { 0%,100% { filter: brightness(1); } 50% { filter: brightness(1.3); } }
        @keyframes hw-pulse { 0%,100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.05); } }
        @keyframes hw-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        @keyframes hw-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes hw-spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes hw-count { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
        @keyframes hw-border { 0%,100% { border-color: rgba(185,32,116,0.3); } 50% { border-color: rgba(185,32,116,0.7); } }
        .hw-bar-anim { animation: hw-bar 1.2s cubic-bezier(0.25,0.46,0.45,0.94) both; transform-origin: left; }
        .hw-glass { background: rgba(255,255,255,0.03); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border: 1px solid rgba(255,255,255,0.08); }
        .hw-glass-highlight { background: rgba(185,32,116,0.06); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border: 2px solid rgba(185,32,116,0.35); }
        .hw-neon-text { text-shadow: 0 0 20px rgba(185,32,116,0.5), 0 0 40px rgba(185,32,116,0.2); }
      `}</style>

      {/* Immersive background */}
      <div className="absolute inset-0">
        {/* Large orbs */}
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(185,32,116,0.12) 0%, transparent 65%)', animation: 'hw-pulse 8s ease-in-out infinite' }} />
        <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(219,39,119,0.08) 0%, transparent 60%)', animation: 'hw-pulse 10s ease-in-out infinite 3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(185,32,116,0.05) 0%, transparent 50%)', animation: 'hw-pulse 6s ease-in-out infinite 1.5s' }} />
        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-20">
        <BackNav />

        {/* === HERO SECTION === */}
        <div className="text-center mb-14 sm:mb-20" style={{ animation: 'hw-enter 0.7s ease-out both' }}>
          {/* Animated clock icon */}
          <div className="relative inline-flex items-center justify-center mb-8">
            <div className="absolute w-28 h-28 rounded-full border border-primary/20" style={{ animation: 'hw-spin-slow 20s linear infinite' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-full" />
            </div>
            <div className="absolute w-20 h-20 rounded-full border border-pink-500/15" style={{ animation: 'hw-spin-slow 15s linear infinite reverse' }}>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1.5 h-1.5 bg-pink-400 rounded-full" />
            </div>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/25 to-pink-500/25 border border-primary/40 flex items-center justify-center" style={{ animation: 'hw-float 4s ease-in-out infinite' }}>
              <Clock className="w-8 h-8 text-primary" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-tight leading-none">
            {isFr ? 'Nos' : 'Our'}{' '}
            <span className="hw-neon-text text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-400 to-rose-400">
              {isFr ? 'Horaires' : 'Hours'}
            </span>
          </h1>
          <p className="text-gray-400 mt-5 text-base sm:text-lg max-w-md mx-auto leading-relaxed">
            {isFr ? 'Du lundi au samedi, des sessions tôt le matin aux entraînements du soir.' : 'Monday to Saturday, from early morning to evening sessions.'}
          </p>
        </div>

        {/* === LIVE STATUS + STATS ROW === */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-14" style={{ animation: 'hw-enter 0.7s ease-out 0.15s both' }}>
          {/* Live status badge */}
          {(() => {
            const d = days[todayIdx];
            const isOpen = d.open && currentHour >= d.start && currentHour <= d.end;
            return (
              <div className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-full border ${isOpen ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`} style={isOpen ? { animation: 'hw-border 3s ease-in-out infinite' } : {}}>
                <div className="relative">
                  <div className={`w-2.5 h-2.5 rounded-full ${isOpen ? 'bg-emerald-400' : 'bg-red-400'}`} />
                  {isOpen && <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />}
                </div>
                <span className={`text-sm font-bold ${isOpen ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isOpen ? (isFr ? `Ouvert maintenant` : `Open now`) : d.open ? (isFr ? `Ouvert aujourd'hui · ${d.hours}` : `Open today · ${d.hours}`) : (isFr ? "Ferme aujourd'hui" : 'Closed today')}
                </span>
              </div>
            );
          })()}

          <div className="hidden sm:block w-px h-6 bg-white/10" />

          {/* Stats pills */}
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-gray-300">{Math.round(totalHours)}h / {isFr ? 'semaine' : 'week'}</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10">
              <Calendar className="w-3.5 h-3.5 text-pink-400" />
              <span className="text-xs font-semibold text-gray-300">6 {isFr ? 'jours' : 'days'} / 7</span>
            </div>
          </div>
        </div>

        {/* === TIME SCALE HEADER (desktop) === */}
        <div className="hidden md:block mb-2" style={{ animation: 'hw-enter 0.7s ease-out 0.25s both' }}>
          <div className="flex items-center gap-3 sm:gap-5 px-4 sm:px-6">
            <div className="hidden sm:block w-10 shrink-0" />
            <div className="w-20 sm:w-28 shrink-0" />
            <div className="flex-1 relative h-5">
              {[6, 8, 10, 12, 14, 16, 18, 20].map(h => (
                <span key={h} className="absolute text-[10px] font-mono text-gray-500/70 -translate-x-1/2" style={{ left: `${((h - SCALE_START) / SCALE_RANGE) * 100}%` }}>
                  {h}h
                </span>
              ))}
            </div>
            <div className="ml-auto md:ml-4 w-[90px] shrink-0" />
          </div>
        </div>

        {/* === SCHEDULE ROWS === */}
        <div className="space-y-2 sm:space-y-3">
          {days.map((d, i) => {
            const isToday = i === todayIdx;
            const Icon = d.icon;
            const barLeft = d.open ? getBarLeft(d.start) : 0;
            const barWidth = d.open ? getBarWidth(d.start, d.end) : 0;
            const isNowInRange = isToday && d.open && currentHour >= d.start && currentHour <= d.end;
            const nowPosition = isNowInRange ? ((currentHour - d.start) / (d.end - d.start)) * 100 : 0;

            return (
              <div
                key={d.day}
                className={`group relative rounded-xl sm:rounded-2xl transition-all duration-500 overflow-hidden ${
                  isToday
                    ? 'hw-glass-highlight shadow-lg shadow-primary/10 scale-[1.01]'
                    : d.open
                      ? 'hw-glass hover:bg-white/[0.05] hover:border-white/15'
                      : 'bg-white/[0.015] border border-white/[0.04] opacity-50'
                }`}
                style={{ animation: `hw-enter 0.5s ease-out ${0.3 + i * 0.08}s both` }}
                data-testid={`horaire-${d.short}`}
              >
                {/* Shimmer on today */}
                {isToday && (
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent" style={{ animation: 'hw-shimmer 4s ease-in-out infinite' }} />
                  </div>
                )}

                <div className="flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5">
                  {/* Icon */}
                  <div className={`hidden sm:flex w-10 h-10 rounded-xl items-center justify-center shrink-0 transition-all duration-300 ${
                    isToday ? 'bg-primary/20 shadow-md shadow-primary/20' : d.open ? 'bg-white/[0.05] group-hover:bg-white/[0.08]' : 'bg-white/[0.02]'
                  }`}>
                    <Icon className={`w-4.5 h-4.5 ${isToday ? 'text-primary' : d.open ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-700'}`} style={isToday ? { animation: 'hw-glow 3s ease-in-out infinite' } : {}} />
                  </div>

                  {/* Day name */}
                  <div className="w-20 sm:w-28 shrink-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm sm:text-base font-bold ${isToday ? 'text-white hw-neon-text' : d.open ? 'text-gray-200' : 'text-gray-600'}`}>
                        {d.day}
                      </span>
                    </div>
                    {isToday && (
                      <span className="inline-block mt-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-primary">
                        {isFr ? "Aujourd'hui" : 'Today'}
                      </span>
                    )}
                  </div>

                  {/* Visual timeline bar (desktop) */}
                  <div className="hidden md:block flex-1 relative h-8">
                    {d.open ? (
                      <>
                        {/* Track */}
                        <div className="absolute inset-y-0 left-0 right-0 flex items-center">
                          <div className="w-full h-1.5 rounded-full bg-white/[0.04]" />
                        </div>
                        {/* Active bar */}
                        <div
                          className="absolute inset-y-0 flex items-center hw-bar-anim"
                          style={{ left: `${barLeft}%`, width: `${barWidth}%`, animationDelay: `${0.5 + i * 0.1}s` }}
                        >
                          <div className={`w-full h-2.5 rounded-full relative overflow-hidden ${
                            isToday
                              ? 'bg-gradient-to-r from-primary via-pink-500 to-rose-400 shadow-md shadow-primary/30'
                              : 'bg-gradient-to-r from-primary/50 via-pink-500/40 to-primary/30'
                          }`}>
                            {/* Shimmer inside bar */}
                            {isToday && (
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent" style={{ animation: 'hw-shimmer 3s ease-in-out infinite' }} />
                            )}
                          </div>
                        </div>
                        {/* Current time marker */}
                        {isNowInRange && (
                          <div
                            className="absolute top-1/2 -translate-y-1/2 z-10"
                            style={{ left: `${barLeft + (barWidth * nowPosition / 100)}%` }}
                          >
                            <div className="relative flex flex-col items-center">
                              <div className="w-3.5 h-3.5 rounded-full bg-white border-2 border-primary shadow-lg shadow-primary/50" style={{ animation: 'hw-pulse 2s ease-in-out infinite' }} />
                            </div>
                          </div>
                        )}
                        {/* Start/End labels on bar */}
                        <div className="absolute -bottom-0.5 flex items-end justify-between pointer-events-none" style={{ left: `${barLeft}%`, width: `${barWidth}%` }}>
                          <span className="text-[9px] font-mono text-gray-500">{d.hours.split(' - ')[0]}</span>
                          <span className="text-[9px] font-mono text-gray-500">{d.hours.split(' - ')[1]}</span>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-center">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-px bg-gray-800" />
                          <span className="text-xs text-gray-600 font-medium">{isFr ? 'Ferme' : 'Closed'}</span>
                          <div className="w-8 h-px bg-gray-800" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Hours text (always visible, primary on mobile) */}
                  <div className="ml-auto md:ml-4 text-right shrink-0">
                    <span className={`text-sm sm:text-base font-bold tabular-nums ${
                      !d.open ? 'text-gray-600 line-through' : isToday ? 'text-primary hw-neon-text' : 'text-gray-200'
                    }`}>
                      {d.hours}
                    </span>
                    {d.open && (
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {Math.round((d.end - d.start) * 10) / 10}h
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom glow line for today */}
                {isToday && <div className="h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />}
              </div>
            );
          })}
        </div>

        {/* === HIGHLIGHTS STRIP === */}
        <div className="mt-10 sm:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-3" style={{ animation: 'hw-enter 0.6s ease-out 1s both' }}>
          {[
            { icon: Zap, value: isFr ? '6h30' : '6:30AM', label: isFr ? 'Ouverture' : 'Opening', color: 'from-amber-500/20 to-orange-500/20', borderColor: 'border-amber-500/30', textColor: 'text-amber-400', iconColor: 'text-amber-400' },
            { icon: Clock, value: '14h', label: isFr ? 'Max / jour' : 'Max / day', color: 'from-primary/20 to-pink-500/20', borderColor: 'border-primary/30', textColor: 'text-primary', iconColor: 'text-primary' },
            { icon: Calendar, value: `${Math.round(totalHours)}h`, label: isFr ? '/ semaine' : '/ week', color: 'from-cyan-500/20 to-blue-500/20', borderColor: 'border-cyan-500/30', textColor: 'text-cyan-400', iconColor: 'text-cyan-400' },
            { icon: ParkingCircle, value: isFr ? 'Gratuit' : 'Free', label: 'Parking', color: 'from-emerald-500/20 to-green-500/20', borderColor: 'border-emerald-500/30', textColor: 'text-emerald-400', iconColor: 'text-emerald-400' },
          ].map((stat, idx) => (
            <div key={idx} className={`relative rounded-xl sm:rounded-2xl overflow-hidden border ${stat.borderColor} bg-gradient-to-br ${stat.color} p-4 sm:p-5 text-center transition-all duration-300 hover:scale-[1.03]`}>
              <stat.icon className={`w-5 h-5 ${stat.iconColor} mx-auto mb-2`} />
              <div className={`text-lg sm:text-xl font-black ${stat.textColor}`} style={{ animation: `hw-count 0.5s ease-out ${1.1 + idx * 0.1}s both` }}>{stat.value}</div>
              <div className="text-[10px] sm:text-xs text-gray-400 font-medium mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* === BOTTOM CTA === */}
        <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 gap-3 sm:gap-4" style={{ animation: 'hw-enter 0.5s ease-out 1.2s both' }}>
          <div className="hw-glass rounded-xl sm:rounded-2xl p-5 flex items-center gap-4">
            <div className="w-11 h-11 bg-primary/15 rounded-xl flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold">{isFr ? 'Adresse' : 'Address'}</p>
              <p className="text-white text-sm font-medium mt-0.5">Route des Tattes d'Oie 95, 1260 Nyon</p>
            </div>
          </div>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="group/bk hw-glass-highlight rounded-xl sm:rounded-2xl p-5 flex items-center gap-4 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300" data-testid="horaires-book-btn">
            <div className="w-11 h-11 bg-primary/25 rounded-xl flex items-center justify-center shrink-0 group-hover/bk:scale-110 transition-transform">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold">{isFr ? 'Sur rendez-vous' : 'By appointment'}</p>
              <p className="text-primary text-sm font-bold mt-0.5 flex items-center gap-1.5">
                {isFr ? 'Reserver maintenant' : 'Book now'}
                <ArrowRight className="w-4 h-4 group-hover/bk:translate-x-1 transition-transform" />
              </p>
            </div>
          </a>
        </div>
      </div>
    </main>
  );
};

// Main App

export default HorairesPage;
